import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router";
import { useCategories, useCreateMarket, useMe } from "../api/hooks";
import { Button, Card, ErrorBox, Field, Input, Select, Textarea, cx } from "../components/ui";
import { chips, fromLocalInput, parseChips, toLocalInput } from "../lib/format";

const MIN_LIQUIDITY = 250;
const MAX_DAYS = 7;

export function NewMarketPage() {
  const me = useMe();
  const cats = useCategories();
  const create = useCreateMarket();
  const navigate = useNavigate();

  const [type, setType] = useState<"BINARY" | "MULTI">("BINARY");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [criteria, setCriteria] = useState("");
  const [description, setDescription] = useState("");
  const [eventAt, setEventAt] = useState(() => toLocalInput(new Date(Date.now() + 24 * 3600_000)));
  const [separateClose, setSeparateClose] = useState(false);
  const [closeAt, setCloseAt] = useState("");
  const [liquidity, setLiquidity] = useState(String(MIN_LIQUIDITY));
  const [outcomes, setOutcomes] = useState(["", ""]);

  const slots = me.data?.createSlotsLeft ?? 0;
  const balance = me.data?.user.balanceM ?? 0;
  const liqM = parseChips(liquidity);
  const maxDate = toLocalInput(new Date(Date.now() + MAX_DAYS * 86_400_000));

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (liqM === null) return;
    const event = fromLocalInput(eventAt);
    const r = await create
      .mutateAsync({
        type, title, category: category || cats.data?.[0]?.slug || "", criteria, description,
        eventAt: event, closeAt: separateClose && closeAt ? fromLocalInput(closeAt) : event, liquidityM: liqM,
        ...(type === "MULTI" ? { outcomes: outcomes.map((o) => o.trim()).filter(Boolean) } : {}),
      })
      .catch(() => null);
    if (r) navigate(`/m/${r.market.id}`);
  }

  if (me.data?.user.role === "admin") return <p className="text-sm text-muted">The admin account can't create markets. Use your member account.</p>;

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <h1 className="text-xl font-semibold">Create a market</h1>
      <p className="text-sm text-muted">
        You seed it with at least {MIN_LIQUIDITY} chips of liquidity and get back whatever's left in the pool when it resolves.
        {" "}{slots > 0 ? <>You can create <strong>{slots}</strong> more right now.</> : <strong className="text-warn">You've hit your creation limit (2 unresolved, 5 per week).</strong>}
      </p>
      <Card>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-1 rounded-lg bg-surface-2 p-1">
            {(["BINARY", "MULTI"] as const).map((t) => (
              <button key={t} type="button" aria-pressed={type === t} onClick={() => setType(t)}
                className={cx("rounded-md py-1.5 text-sm font-medium", type === t ? "bg-surface shadow-sm" : "text-muted")}>
                {t === "BINARY" ? "Yes / No" : "Multiple choice"}
              </button>
            ))}
          </div>

          <Field label="Question">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Will Oxford win the Boat Race?" required minLength={5} maxLength={120} />
          </Field>

          {type === "MULTI" && (
            <Field label="Outcomes" hint="2–20 options, exactly one of which will happen.">
              <div className="space-y-2">
                {outcomes.map((o, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={o} onChange={(e) => setOutcomes(outcomes.map((x, j) => (j === i ? e.target.value : x)))} placeholder={`Option ${i + 1}`} maxLength={60} required />
                    {outcomes.length > 2 && <Button variant="ghost" onClick={() => setOutcomes(outcomes.filter((_, j) => j !== i))} aria-label="Remove option">✕</Button>}
                  </div>
                ))}
                {outcomes.length < 20 && <Button variant="secondary" onClick={() => setOutcomes([...outcomes, ""])}>Add option</Button>}
              </div>
            </Field>
          )}

          <Field label={type === "BINARY" ? "Resolves YES if…" : "How it resolves"} hint="Shown to voters. Be specific: what source, what counts.">
            <Textarea value={criteria} onChange={(e) => setCriteria(e.target.value)} required maxLength={1000} />
          </Field>

          <Field label="Category">
            <Select value={category || cats.data?.[0]?.slug || ""} onChange={(e) => setCategory(e.target.value)}>
              {cats.data?.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </Select>
          </Field>

          <Field label="When is the event?" hint="Trading closes at this time unless you choose otherwise. Max 7 days out.">
            <Input type="datetime-local" value={eventAt} onChange={(e) => setEventAt(e.target.value)} max={maxDate} required />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={separateClose} onChange={(e) => setSeparateClose(e.target.checked)} /> Close trading earlier than the event
          </label>
          {separateClose && (
            <Field label="Trading closes">
              <Input type="datetime-local" value={closeAt} onChange={(e) => setCloseAt(e.target.value)} max={eventAt} required />
            </Field>
          )}

          <Field label="Description (optional)">
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} />
          </Field>

          <Field label="Liquidity (chips)" hint={`More liquidity means prices move less per trade. Your balance: ${chips(balance)}.`}>
            <Input inputMode="decimal" value={liquidity} onChange={(e) => setLiquidity(e.target.value)} required />
          </Field>

          <ErrorBox error={create.error} />
          <Button type="submit" className="w-full" disabled={create.isPending || slots === 0 || liqM === null || liqM < MIN_LIQUIDITY * 1000 || liqM > balance}>
            {create.isPending ? "Creating…" : `Create market (${liqM !== null ? chips(liqM) : "?"} chips)`}
          </Button>
        </form>
      </Card>
      <p className="text-center text-xs text-muted"><Link to="/">Cancel</Link></p>
    </div>
  );
}
